<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { Plus } from 'lucide-vue-next';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';

import type { IncentiveFilters as IncentiveFiltersType, IncentiveListItem } from '../types';

import { setCachedIncentiveListItem } from '../cache/incentiveListItemCache';
import IncentiveFilters from '../components/IncentiveFilters.vue';
import IncentiveTable from '../components/IncentiveTable.vue';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { mapIncentiveFiltersToApiParams, mapIncentiveListResponse } from '../mappers/incentiveMappers';
import { parseIncentiveApiError } from '../utils';

const router = useRouter();

const isLoading = ref(true);
const incentives = ref<IncentiveListItem[]>([]);
const filters = ref<IncentiveFiltersType>({ status: '' });
const currentPage = ref(1);
const pageSize = ref(DEFAULT_PAGE_SIZE);
const totalCount = ref(0);
const totalPages = ref(0);

const hasNext = computed(() => currentPage.value < totalPages.value);

async function loadIncentives() {
  isLoading.value = true;

  try {
    const data = await campaignManagementService.listIncentives({
      page: currentPage.value,
      page_size: pageSize.value,
      ...mapIncentiveFiltersToApiParams(filters.value),
    });

    const mapped = mapIncentiveListResponse(data);
    incentives.value = mapped.incentives;
    totalCount.value = mapped.totalItems;
    totalPages.value = mapped.totalPages;
    currentPage.value = mapped.page;
  } catch (error) {
    toast.error(parseIncentiveApiError(error).message);
  } finally {
    isLoading.value = false;
  }
}

function reloadFromFirstPage() {
  if (currentPage.value === 1) {
    loadIncentives();
  } else {
    currentPage.value = 1;
  }
}

function onApplyFilters(newFilters: IncentiveFiltersType) {
  filters.value = newFilters;
  reloadFromFirstPage();
}

function onClearFilters() {
  filters.value = { status: '' };
  reloadFromFirstPage();
}

function onPageChange(page: number) {
  currentPage.value = page;
}

function onPageSizeChange(size: number) {
  pageSize.value = size;
  reloadFromFirstPage();
}

function onViewIncentive(incentive: IncentiveListItem) {
  setCachedIncentiveListItem(incentive);
  router.push({ path: '/incentive-detail', query: { id: incentive.id } });
}

watch(currentPage, () => {
  loadIncentives();
}, { immediate: true });

onMounted(() => {
  document.title = 'Incentivos Revalida';
});
</script>

<template>
  <DashboardLayout
    title="Revalida Instalação"
    description="Incentivos"
    subtitle="Crie e gerencie os incentivos utilizados pelas campanhas de revalidação."
  >
    <template #actions>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-primary" @click="router.push('/incentive-new')">
          <Plus class="icon icon-sm me-1" />
          Novo Incentivo
        </button>
      </div>
    </template>

    <div class="row row-cards">
      <IncentiveFilters
        @apply="onApplyFilters"
        @clear="onClearFilters"
      />

      <IncentiveTable
        :rows="incentives"
        :is-loading="isLoading"
        :current-page="currentPage"
        :has-next="hasNext"
        :page-size="pageSize"
        :total-count="totalCount"
        @page-change="onPageChange"
        @page-size-change="onPageSizeChange"
        @view="onViewIncentive"
      />
    </div>
  </DashboardLayout>
</template>
